import { useContext, useState } from 'react'
import { Button, DialogContent, Grid, Typography } from '@mui/material'
import { Form, Formik } from 'formik'
import { EnqueueSnackbar, useSnackbar } from 'notistack'
import * as Yup from 'yup'

import addPhotoImage from '../../images/add-photo.png'
import { useHost } from '../../providers/app-provider'
import { DeviceTableRow } from '../../routes/accounts/components/devices-tab-panel/types'
import AccountsContext from '../../routes/accounts/context'
import DeviceContext from '../../routes/devices/context'
import Api from '../../utils/api'
import { getFirmwareVersion } from '../../utils/helpers'
import useLoadDevicePhoto from '../../utils/hooks/useLoadDevicePhoto'
import useObjectUrlCleanup from '../../utils/hooks/useObjectUrlCleanup'
import useSharedStyles from '../../utils/shared.styles'
import getUseCaseMatrix from '../../utils/usecase-matrix'
import DeviceChangeSolutionModal from '../device-change-solution-modal'
import DevicePhotoModal from '../device-photo-modal'
import DialogWithTitle from '../dialog-with-title'

import DeviceEditFields, { EditDeviceFormValues } from './device-edit-fields'
import useStyles from './index.styles'

const updateDeviceUsecaseLocation = async ({
  deviceId,
  useCase,
  location,
  notes,
  building,
  floor,
  setDevice,
  enqueueSnackbar,
  closeModal,
  host,
}: {
  deviceId: number
  useCase: string
  location: string
  notes: string
  building: string
  floor: string
  setDevice: React.Dispatch<React.SetStateAction<IDeviceAPIObject | undefined>>
  enqueueSnackbar: EnqueueSnackbar
  closeModal: () => void
  host: string
}) => {
  const res = await Api.patch(`${host}/api/v2/central/devices/${deviceId}/usecaselocation`, {
    location,
    notes,
    building,
    floor,
    usecases: [useCase],
  })
  if (res.ok) {
    setDevice((prev: IDeviceAPIObject | undefined) =>
      prev
        ? { ...prev, deviceUsecase: useCase as DeviceUseCaseType, area: location, metadata: { ...prev.metadata, device_location_note: notes }, building, floor }
        : prev
    )
    enqueueSnackbar(`Device updated successfully.`)
    closeModal()
  } else {
    enqueueSnackbar(`Could not update device.`, { variant: 'error' })
  }
}

interface DeviceEditModalProps {
  device: DeviceTableRow | IDeviceAPIObject
  setDevice: React.Dispatch<React.SetStateAction<IDeviceAPIObject | undefined>>
  open: boolean
  onClose: () => void
  folderTheme: IFolderTheme | undefined
}

const DeviceEditModal = ({ device, setDevice, open, onClose, folderTheme }: DeviceEditModalProps) => {
  const classes = useStyles
  const sharedClasses = useSharedStyles

  const { enqueueSnackbar } = useSnackbar()
  const host = useHost()

  const { setDevicePhotoRefreshCounter: setAccountsPhotoRefreshCounter } = useContext(AccountsContext)
  const { setDevicePhotoRefreshCounter } = useContext(DeviceContext)

  const [changeSolutionModal, setChangeSolutionModal] = useState<boolean>(false)
  const [showDevicePhotoModal, setShowDevicePhotoModal] = useState<boolean>(false)
  const [hasPhoto, setHasPhoto] = useState<boolean>(device?.hasPhoto || false)

  const shouldLoad = !!device?.hasPhoto
  const { photoPath, setPhotoPath } = useLoadDevicePhoto(device?.id, shouldLoad, 'thumb')

  // Cleanup object URL when component unmounts or photoPath changes
  useObjectUrlCleanup(photoPath)

  if (!device) return null // Ensure device is defined before proceeding

  const deviceLocation = device.area || device.metadata?.device_location || ''
  const deviceLocationNotes = device.metadata?.device_location_note || ''

  const initialValues: EditDeviceFormValues = {
    useCase: device.deviceUsecase,
    location: deviceLocation,
    notes: deviceLocationNotes,
    building: device.building,
    floor: device.floor,
  }

  const validationSchema = Yup.object().shape({
    useCase: Yup.string().trim().required('Required'),
    location: Yup.string().trim().required('Required'),
    floor: Yup.string().max(4, 'Floor must be four or fewer characters'),
    notes: Yup.string().trim().required('Required'),
  })

  const submitForm = async (values: EditDeviceFormValues) => {
    if (values.useCase !== device.deviceUsecase) {
      setChangeSolutionModal(true)
    } else {
      await updateDeviceUsecaseLocation({
        deviceId: device.id,
        useCase: values.useCase,
        location: values.location,
        notes: values.notes,
        building: values.building,
        floor: values.floor,
        setDevice,
        enqueueSnackbar,
        closeModal: () => onClose(),
        host,
      })
    }
  }

  const handleShowDevicePhotoModal = () => {
    setShowDevicePhotoModal(true)
  }

  const onSavePhoto = (uploadedPhoto: string) => {
    setPhotoPath(uploadedPhoto)
    setHasPhoto(true)
    setDevice((prev) => (prev ? { ...prev, hasPhoto: true } : prev)) // Update device state to reflect photo change

    // Increment counter in both contexts to trigger photo refresh on device screen
    setAccountsPhotoRefreshCounter((prev) => prev + 1)
    setDevicePhotoRefreshCounter((prev) => prev + 1)

    setShowDevicePhotoModal(false)
    enqueueSnackbar('Device photo updated successfully.', { variant: 'success' })
  }

  return (
    <>
      <DialogWithTitle open={open} title="Device Solution + Location" setOpen={onClose}>
        <DialogContent>
          {hasPhoto && photoPath ? (
            <Grid container={true} spacing={1} sx={[sharedClasses.centerContentVertical, { marginBottom: 1 }]}>
              <Grid>
                <img src={photoPath} alt="Photo of device" style={classes.thumbnail} />
              </Grid>
              <Grid>
                <Button variant="text" size="small" sx={classes.photoBtn} onClick={handleShowDevicePhotoModal}>
                  Change Photo
                </Button>
              </Grid>
            </Grid>
          ) : (
            <Grid container={true} spacing={1} sx={[sharedClasses.centerContentVertical, { marginBottom: 1 }]}>
              <Grid>
                <img src={addPhotoImage} alt="No photo" />
              </Grid>
              <Grid>
                <Button variant="text" size="small" sx={classes.photoBtn} onClick={handleShowDevicePhotoModal}>
                  Add Photo
                </Button>
              </Grid>
            </Grid>
          )}

          <Grid container={true} sx={[sharedClasses.centerContentVertical, { gap: 1 }]}>
            <Grid>
              <Typography variant="body1">{device.vanity}</Typography>
            </Grid>
            {getFirmwareVersion(device.firmwareVersion) && (
              <Grid sx={classes.pill}>
                <Typography variant="caption">v{device.firmwareVersion}</Typography>
              </Grid>
            )}
            {device.deviceUsecase && (
              <Grid sx={classes.pill}>
                <Typography variant="caption">{getUseCaseMatrix()[device.deviceUsecase]}</Typography>
              </Grid>
            )}
          </Grid>

          <Formik initialValues={initialValues} onSubmit={submitForm} validationSchema={validationSchema}>
            {({ values, dirty, isValid, isSubmitting }) => (
              <Form>
                <Grid container={true} spacing={2}>
                  <DeviceEditFields device={device} initialValues={initialValues} folderTheme={folderTheme} />

                  <Grid
                    container={true}
                    size={12}
                    sx={{
                      justifyContent: 'flex-end',
                      gap: 2,
                      marginTop: 1,
                    }}
                  >
                    <Button variant="outlined" onClick={onClose}>
                      Cancel
                    </Button>
                    <Button variant="contained" type="submit" disabled={!dirty || !isValid || isSubmitting}>
                      Save
                    </Button>
                  </Grid>
                </Grid>

                <DeviceChangeSolutionModal
                  open={changeSolutionModal}
                  onCancel={() => setChangeSolutionModal(false)}
                  onContinue={async () => {
                    await updateDeviceUsecaseLocation({
                      deviceId: device.id,
                      useCase: values.useCase,
                      location: values.location,
                      notes: values.notes,
                      building: values.building,
                      floor: values.floor,
                      setDevice,
                      enqueueSnackbar,
                      closeModal: () => {
                        setChangeSolutionModal(false)
                        onClose()
                      },
                      host,
                    })
                  }}
                />
              </Form>
            )}
          </Formik>
        </DialogContent>
      </DialogWithTitle>
      <DevicePhotoModal
        deviceId={device.id}
        open={showDevicePhotoModal}
        onClose={() => setShowDevicePhotoModal(false)}
        photoPath={photoPath}
        onSave={onSavePhoto}
      />
    </>
  )
}

export default DeviceEditModal
